import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { clinicaService } from '../services/clinicaService'
import Table, { TableHeader, TableBody, TableRow, TableCell, TableHead } from '../components/ui/Table'
import Button from '../components/ui/Button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Input from '../components/ui/Input'
import Listbox from '../components/ui/Listbox'
import Disclosure from '../components/ui/Disclosure'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import { Building2, Plus, Search, RefreshCw, AlertCircle, Edit, Trash2, Eye, X, AlertTriangle, Settings } from 'lucide-react'
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CARTO_ATTRIBUTION, CARTO_LIGHT_TILE_URL, CARTO_TILE_SUBDOMAINS } from '../constants/mapTiles'

function parseCoordinate(value) {
  if (value == null) return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const cleaned = String(value).trim().replace(',', '.')
  const parsed = Number.parseFloat(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

function getClinicCoordinates(clinica = {}) {
  const latCandidates = [
    clinica.latitud,
    clinica.latitude,
    clinica.lat,
    clinica.coordenadas?.latitud,
    clinica.coordenadas?.latitude,
    clinica.coordenadas?.lat,
    clinica.ubicacion?.latitud,
    clinica.ubicacion?.latitude,
    clinica.ubicacion?.lat
  ]

  const lngCandidates = [
    clinica.longitud,
    clinica.longitude,
    clinica.lng,
    clinica.lon,
    clinica.coordenadas?.longitud,
    clinica.coordenadas?.longitude,
    clinica.coordenadas?.lng,
    clinica.coordenadas?.lon,
    clinica.ubicacion?.longitud,
    clinica.ubicacion?.longitude,
    clinica.ubicacion?.lng,
    clinica.ubicacion?.lon
  ]

  const geoCoords = clinica.ubicacion?.coordinates || clinica.coordenadas?.coordinates
  if (Array.isArray(geoCoords) && geoCoords.length >= 2) {
    const lng = parseCoordinate(geoCoords[0])
    const lat = parseCoordinate(geoCoords[1])
    if (lat != null && lng != null) return { lat, lng }
  }

  const lat = latCandidates.map(parseCoordinate).find((v) => v != null)
  const lng = lngCandidates.map(parseCoordinate).find((v) => v != null)

  if (lat == null || lng == null) return null
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
  return { lat, lng }
}

function MapBoundsUpdater({ points = [] }) {
  const map = useMap()

  useEffect(() => {
    if (!map || points.length === 0) return
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13)
      return
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]))
    map.fitBounds(bounds, { padding: [34, 34], maxZoom: 13 })
  }, [map, points])

  return null
}

export default function Clinicas() {
  const { user, refreshUser } = useAuth()
  const {
    isDueno,
    isVeterinario,
    isVeterinarioAdmin,
    userClinicaId,
    tieneClinica,
    canEditClinica,
    canCreateClinica,
    isAdmin,
  } = usePermissions()
  const veterinarioData = user?.veterinarios?.[0]

  const [clinicas, setClinicas] = useState([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    search: '',
    ciudad: '',
    pais: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showEmergenciasModal, setShowEmergenciasModal] = useState(false)
  const [selectedClinica, setSelectedClinica] = useState(null)
  const [emergenciasConfig, setEmergenciasConfig] = useState({
    acepta_emergencias_auto: true,
    duracion_suscripcion_temporal: 7,
    requiere_aprobacion_emergencia: false,
    alcance_emergencia: 'MASCOTA'
  })
  const [formData, setFormData] = useState({
    nombre: '',
    razon_social: '',
    ruc: '',
    direccion: '',
    ciudad: '',
    codigo_postal: '',
    pais: 'Uruguay',
    telefono: '',
    email: '',
    sitio_web: ''
  })
  const [editFormData, setEditFormData] = useState({
    nombre: '',
    razon_social: '',
    ruc: '',
    direccion: '',
    ciudad: '',
    codigo_postal: '',
    pais: 'Uruguay',
    telefono: '',
    email: '',
    sitio_web: '',
    descripcion: ''
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchClinicas()
  }, [pagination.current_page, filtros])

  const fetchClinicas = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.search && { search: filtros.search }),
        ...(filtros.ciudad && { ciudad: filtros.ciudad }),
        ...(filtros.pais && { pais: filtros.pais })
      }

      // Si es veterinario admin, solo obtener su clínica
      if (isVeterinarioAdmin && userClinicaId) {
        params.id_clinica = userClinicaId
      }

      const response = await clinicaService.getAll(params)
      
      if (response.success) {
        if (response.data.data) {
          setClinicas(response.data.data)
          setPagination({
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 15,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1
          })
        } else {
          setClinicas(Array.isArray(response.data) ? response.data : [])
        }
      } else {
        setError(response.error || 'Error al cargar clínicas')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching clínicas:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const response = await clinicaService.create(formData)
      
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        // Si es veterinario, recargar usuario para actualizar el estado de tieneClinica
        if (isVeterinario) {
          await refreshUser()
        }
        fetchClinicas()
      } else {
        setError(response.error || 'Error al crear clínica')
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || 
                      'Error al crear clínica'
      setError(errorMsg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    const confirmMessage = isVeterinarioAdmin 
      ? '¿Estás seguro de que quieres desactivar tu clínica? Esta acción desactivará tu clínica y tu registro como veterinario.'
      : '¿Estás seguro de que quieres eliminar esta clínica?'
    
    if (!confirm(confirmMessage)) {
      return
    }

    try {
      const response = await clinicaService.delete(id)
      if (response.success) {
        // Si es veterinario, recargar usuario para actualizar el estado
        if (isVeterinario) {
          await refreshUser()
        }
        fetchClinicas()
      } else {
        setError(response.error || 'Error al eliminar clínica')
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || 
                      'Error al eliminar clínica'
      setError(errorMsg)
    }
  }

  const handleEdit = (clinica) => {
    setSelectedClinica(clinica)
    setEditFormData({
      nombre: clinica.nombre || '',
      razon_social: clinica.razon_social || clinica.razonSocial || '',
      ruc: clinica.ruc || '',
      direccion: clinica.direccion || '',
      ciudad: clinica.ciudad || '',
      codigo_postal: clinica.codigo_postal || clinica.codigoPostal || '',
      pais: clinica.pais || clinica.ubicacion?.pais || 'Uruguay',
      telefono: clinica.telefono || '',
      email: clinica.email || '',
      sitio_web: clinica.sitio_web || clinica.sitioWeb || '',
      descripcion: clinica.descripcion || ''
    })
    setError(null) // Limpiar errores previos
    setShowEditModal(true)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const clinicaId = selectedClinica.id_clinica || selectedClinica.idClinica
      const response = await clinicaService.update(clinicaId, editFormData)
      
      if (response.success) {
        setShowEditModal(false)
        setSelectedClinica(null)
        fetchClinicas()
      } else {
        const errorMsg = response.error || response.message || 'Error al actualizar clínica'
        setError(errorMsg)
        if (response.details) {
          console.error('Detalles del error:', response.details)
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || 
                      'Error al actualizar clínica'
      setError(errorMsg)
      console.error('Error completo:', err)
      if (err.response?.data?.details) {
        console.error('Detalles del error:', err.response.data.details)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfigurarEmergencias = (clinica) => {
    setSelectedClinica(clinica)
    setEmergenciasConfig({
      acepta_emergencias_auto: clinica.acepta_emergencias_auto ?? true,
      duracion_suscripcion_temporal: clinica.duracion_suscripcion_temporal ?? 7,
      requiere_aprobacion_emergencia: clinica.requiere_aprobacion_emergencia ?? false,
      alcance_emergencia: clinica.alcance_emergencia ?? 'MASCOTA'
    })
    setError(null)
    setShowEmergenciasModal(true)
  }

  const handleSaveEmergenciasConfig = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    
    try {
      const clinicaId = selectedClinica.id_clinica || selectedClinica.idClinica
      const response = await clinicaService.configurarEmergencias(clinicaId, emergenciasConfig)
      
      if (response.success) {
        setShowEmergenciasModal(false)
        setSelectedClinica(null)
        fetchClinicas()
      } else {
        setError(response.error || 'Error al configurar política de emergencias')
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || 
                      'Error al configurar política de emergencias'
      setError(errorMsg)
    } finally {
      setSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      nombre: '',
      razon_social: '',
      ruc: '',
      direccion: '',
      ciudad: '',
      codigo_postal: '',
      pais: 'Uruguay',
      telefono: '',
      email: '',
      sitio_web: ''
    })
  }

  const ciudadesUnicas = [...new Set(clinicas.map(c => c.ciudad).filter(Boolean))]
  const paisesUnicas = [...new Set(
    [...clinicas.map((c) => c.pais).filter(Boolean), 'Uruguay', 'Argentina', 'Brasil', 'Paraguay']
  )].sort((a, b) => a.localeCompare(b, 'es'))

  const clinicsForMap = useMemo(() => (
    clinicas
      .map((clinica) => {
        const coords = getClinicCoordinates(clinica)
        if (!coords) return null
        return {
          id: clinica.id_clinica || clinica.idClinica || clinica.id,
          clinica,
          ...coords
        }
      })
      .filter(Boolean)
  ), [clinicas])

  const mapCenter = useMemo(() => {
    if (clinicsForMap.length > 0) {
      const avgLat = clinicsForMap.reduce((sum, c) => sum + c.lat, 0) / clinicsForMap.length
      const avgLng = clinicsForMap.reduce((sum, c) => sum + c.lng, 0) / clinicsForMap.length
      return [avgLat, avgLng]
    }
    // Montevideo fallback
    return [-34.9011, -56.1645]
  }, [clinicsForMap])

  const mapPinIcon = useMemo(() => L.divIcon({
    className: 'clinic-pin',
    html: '<div style="width:18px;height:18px;border-radius:9999px;background:#7abcc4;border:2px solid white;box-shadow:0 3px 8px rgba(0,0,0,.25)"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9]
  }), [])

  const clearFilters = () => {
    setFiltros({ search: '', ciudad: '', pais: '' })
  }

  const hasActiveFilters = filtros.search || filtros.ciudad || filtros.pais

  if (loading && clinicas.length === 0) {
    return <LoadingState message="Cargando clínicas..." size="lg" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {isDueno ? 'Mi Clínica' : isVeterinarioAdmin ? 'Mi Clínica' : 'Clínicas Veterinarias'}
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {isDueno 
              ? 'Información de tu clínica veterinaria'
              : isVeterinarioAdmin
              ? 'Gestiona la información de tu clínica'
              : 'Gestión de clínicas del sistema'}
          </p>
        </div>
        {canCreateClinica && (
          <Button onClick={() => {
            setShowCreateModal(true)
          }} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            {isVeterinario && !tieneClinica ? 'Crear Mi Clínica' : 'Nueva Clínica'}
          </Button>
        )}
        {!canCreateClinica && isVeterinario && tieneClinica && (
          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Ya tienes una clínica creada
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filtros - Solo para no DUEÑO y no veterinario admin */}
      {!isDueno && !isVeterinarioAdmin && (
        <Disclosure 
          title="Filtros de Búsqueda"
          defaultOpen={hasActiveFilters}
          className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
        >
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Input
              label="Búsqueda"
              leftIcon={Search}
              placeholder="Nombre, razón social..."
              value={filtros.search}
              onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
            />
            <Listbox
              label="Ciudad"
              options={[
                { value: '', label: 'Todas' },
                ...ciudadesUnicas.map((ciudad) => ({
                  value: ciudad,
                  label: ciudad
                }))
              ]}
              value={filtros.ciudad}
              onChange={(value) => setFiltros({ ...filtros, ciudad: value })}
            />
            <Listbox
              label="País"
              options={[
                { value: '', label: 'Todos' },
                ...paisesUnicas.map((p) => ({
                  value: p,
                  label: p
                }))
              ]}
              value={filtros.pais}
              onChange={(value) => setFiltros({ ...filtros, pais: value })}
            />
            <div className="flex items-end">
              <Button onClick={fetchClinicas} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpiar Filtros
              </Button>
            </div>
          )}
        </div>
      </Disclosure>
      )}

      {/* Tabla */}
      <Card>
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Mapa de clínicas</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {clinicsForMap.length} pin{clinicsForMap.length === 1 ? '' : 'es'}
            </span>
          </div>
          <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            {clinicsForMap.length > 0 ? (
              <MapContainer
                center={mapCenter}
                zoom={11}
                scrollWheelZoom={false}
                style={{ height: '360px', width: '100%' }}
              >
                <MapBoundsUpdater points={clinicsForMap} />
                <TileLayer
                  attribution={CARTO_ATTRIBUTION}
                  url={CARTO_LIGHT_TILE_URL}
                  subdomains={CARTO_TILE_SUBDOMAINS}
                  maxZoom={20}
                />
                {clinicsForMap.map(({ id, lat, lng, clinica }) => (
                  <Marker key={`map-${id}-${lat}-${lng}`} position={[lat, lng]} icon={mapPinIcon}>
                    <Popup>
                      <div className="text-xs">
                        <strong>{clinica.nombre || 'Clínica'}</strong>
                        <br />
                        {[clinica.direccion, clinica.ciudad, clinica.pais].filter(Boolean).join(' · ') || 'Sin dirección'}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            ) : (
              <div className="h-[220px] bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center text-center px-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No hay coordenadas disponibles para mostrar en el mapa.
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {clinicas.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No hay clínicas registradas"
          description="No se encontraron clínicas con los filtros seleccionados."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto -mx-6 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="hidden lg:table-cell">Razón Social</TableHead>
                  <TableHead className="hidden xl:table-cell">RUC</TableHead>
                  <TableHead className="hidden md:table-cell">Dirección</TableHead>
                  <TableHead className="hidden sm:table-cell">Ciudad</TableHead>
                  <TableHead className="hidden sm:table-cell">País</TableHead>
                  <TableHead className="hidden md:table-cell">Teléfono</TableHead>
                  <TableHead className="hidden lg:table-cell">Email</TableHead>
                  <TableHead>Habilitación</TableHead>
                  {(canEditClinica || isVeterinarioAdmin) && (
                    <TableHead>Acciones</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {clinicas.map((clinica) => (
                  <TableRow key={clinica.id_clinica || clinica.idClinica}>
                    <TableCell className="font-medium">{clinica.nombre}</TableCell>
                    <TableCell className="hidden lg:table-cell">{clinica.razon_social || clinica.razonSocial || '-'}</TableCell>
                    <TableCell className="hidden xl:table-cell">{clinica.ruc || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{clinica.direccion || '-'}</TableCell>
                    <TableCell className="hidden sm:table-cell">{clinica.ciudad || '-'}</TableCell>
                    <TableCell className="hidden sm:table-cell">{clinica.pais || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{clinica.telefono || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell">{clinica.email || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={clinica.activa ? 'success' : 'danger'}>
                        {clinica.activa ? 'Habilitada' : 'Deshabilitada'}
                      </Badge>
                    </TableCell>
                    {(() => {
                      const clinicaId = clinica.id_clinica || clinica.idClinica
                      const esMiClinica = isVeterinarioAdmin && (clinicaId === userClinicaId)
                      const mostrarAcciones = canEditClinica || esMiClinica
                      
                      return mostrarAcciones ? (
                        <TableCell>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEdit(clinica)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                              title="Editar"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            {/* Configurar emergencias solo para veterinarios admin de su clínica o ADMIN/SUPERADMIN */}
                            {(isVeterinarioAdmin && esMiClinica) || isAdmin ? (
                              <button
                                onClick={() => handleConfigurarEmergencias(clinica)}
                                className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300"
                                title="Configurar Emergencias"
                              >
                                <AlertTriangle className="w-5 h-5" />
                              </button>
                            ) : null}
                            {/* Permitir eliminar si es ADMIN/SUPERADMIN o si es veterinario admin de esta clínica */}
                            {(isAdmin || esMiClinica) && (
                              <button
                                onClick={() => handleDelete(clinicaId)}
                                className="text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                title="Eliminar"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      ) : null
                    })()}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>
        </Card>
      )}

      {/* Paginación */}
      {pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
            <span className="font-medium">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> de{' '}
            <span className="font-medium">{pagination.total}</span> resultados
          </div>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
            >
              Anterior
            </Button>
            <div className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              Página {pagination.current_page} de {pagination.last_page}
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal Crear Clínica */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} size="lg">
        <ModalHeader onClose={() => { setShowCreateModal(false); resetForm(); }}>
          {isVeterinario && !tieneClinica ? 'Crear Mi Clínica' : 'Nueva Clínica'}
        </ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nombre"
                required
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
              <Input
                label="Razón Social"
                required
                value={formData.razon_social}
                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
              />
              <Input
                label="RUC"
                required
                value={formData.ruc}
                onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
              />
              <Input
                label="Teléfono"
                type="tel"
                required
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              />
              <Input
                label="Email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <Input
                label="Sitio Web"
                type="url"
                value={formData.sitio_web}
                onChange={(e) => setFormData({ ...formData, sitio_web: e.target.value })}
              />
              <div className="md:col-span-2">
                <Input
                  label="Dirección"
                  required
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                />
              </div>
              <Input
                label="Ciudad"
                required
                value={formData.ciudad}
                onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
              />
              <Input
                label="Código Postal"
                value={formData.codigo_postal}
                onChange={(e) => setFormData({ ...formData, codigo_postal: e.target.value })}
              />
              <Input
                label="País"
                required
                value={formData.pais}
                onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Crear Clínica
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Editar Clínica */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedClinica(null); setError(null); }} size="lg">
        <ModalHeader onClose={() => { setShowEditModal(false); setSelectedClinica(null); setError(null); }}>
          Editar Clínica
        </ModalHeader>
        <form onSubmit={handleUpdate}>
          <ModalContent>
            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Nombre"
                  required
                  value={editFormData.nombre}
                  onChange={(e) => setEditFormData({ ...editFormData, nombre: e.target.value })}
                />
              </div>
              <Input
                label="Razón Social"
                value={editFormData.razon_social}
                onChange={(e) => setEditFormData({ ...editFormData, razon_social: e.target.value })}
              />
              <Input
                label="RUC"
                value={editFormData.ruc}
                onChange={(e) => setEditFormData({ ...editFormData, ruc: e.target.value })}
              />
              <div className="md:col-span-2">
                <Input
                  label="Dirección"
                  required
                  value={editFormData.direccion}
                  onChange={(e) => setEditFormData({ ...editFormData, direccion: e.target.value })}
                />
              </div>
              <Input
                label="Ciudad"
                required
                value={editFormData.ciudad}
                onChange={(e) => setEditFormData({ ...editFormData, ciudad: e.target.value })}
              />
              <Input
                label="Código postal"
                value={editFormData.codigo_postal}
                onChange={(e) => setEditFormData({ ...editFormData, codigo_postal: e.target.value })}
              />
              <Input
                label="País"
                required
                value={editFormData.pais}
                onChange={(e) => setEditFormData({ ...editFormData, pais: e.target.value })}
              />
              <Input
                label="Teléfono"
                type="tel"
                value={editFormData.telefono}
                onChange={(e) => setEditFormData({ ...editFormData, telefono: e.target.value })}
              />
              <Input
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
              <Input
                label="Sitio web"
                type="url"
                value={editFormData.sitio_web}
                onChange={(e) => setEditFormData({ ...editFormData, sitio_web: e.target.value })}
              />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Descripción
                </label>
                <textarea
                  value={editFormData.descripcion}
                  onChange={(e) => setEditFormData({ ...editFormData, descripcion: e.target.value })}
                  rows={3}
                  className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-0 px-3 py-2 text-sm"
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowEditModal(false); setSelectedClinica(null); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Actualizar Clínica
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Configurar Emergencias */}
      <Modal isOpen={showEmergenciasModal} onClose={() => { setShowEmergenciasModal(false); setSelectedClinica(null); setError(null); }} size="lg">
        <ModalHeader onClose={() => { setShowEmergenciasModal(false); setSelectedClinica(null); setError(null); }}>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            <span>Configurar Política de Emergencias</span>
          </div>
        </ModalHeader>
        <form onSubmit={handleSaveEmergenciasConfig}>
          <ModalContent>
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-700 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}
            
            <div className="space-y-6">
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                <p className="text-sm text-orange-800 dark:text-orange-300">
                  <strong>Clínica:</strong> {selectedClinica?.nombre || 'N/A'}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900 dark:text-white">
                      Aceptar emergencias automáticamente
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Si está activado, se crearán suscripciones temporales automáticamente cuando se soliciten citas de emergencia
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={emergenciasConfig.acepta_emergencias_auto}
                    onChange={(e) => setEmergenciasConfig({ ...emergenciasConfig, acepta_emergencias_auto: e.target.checked })}
                    className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 dark:focus:ring-orange-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>

                {emergenciasConfig.acepta_emergencias_auto && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Duración de suscripción temporal (días)
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={emergenciasConfig.duracion_suscripcion_temporal}
                        onChange={(e) => setEmergenciasConfig({ ...emergenciasConfig, duracion_suscripcion_temporal: parseInt(e.target.value) || 7 })}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Número de días que durará la suscripción temporal antes de expirar automáticamente
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-900 dark:text-white">
                          Requerir aprobación para emergencias
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Si está activado, las suscripciones temporales requerirán aprobación manual antes de activarse
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={emergenciasConfig.requiere_aprobacion_emergencia}
                        onChange={(e) => setEmergenciasConfig({ ...emergenciasConfig, requiere_aprobacion_emergencia: e.target.checked })}
                        className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 dark:focus:ring-orange-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Alcance de la suscripción temporal
                      </label>
                      <Listbox
                        options={[
                          { value: 'MASCOTA', label: 'Solo la mascota específica' },
                          { value: 'DUENO', label: 'Todas las mascotas del dueño' }
                        ]}
                        value={emergenciasConfig.alcance_emergencia}
                        onChange={(value) => setEmergenciasConfig({ ...emergenciasConfig, alcance_emergencia: value })}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {emergenciasConfig.alcance_emergencia === 'MASCOTA' 
                          ? 'La suscripción temporal solo aplicará a la mascota específica de la emergencia'
                          : 'La suscripción temporal aplicará a todas las mascotas del dueño'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowEmergenciasModal(false); setSelectedClinica(null); setError(null); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Guardar Configuración
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  )
}
